import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {DocumentInfo, isDocumentInfo, StepInfo} from 'procedures/design/ProcedureMethodAnalysisTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Badge, {BadgeColor} from 'ui-library/Badge';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as DateUtil from 'util/DateUtil';
import {ellipsize} from 'util/NameUtil';

// These constants are appropriate for use within ProcedureMethodAnalysisSourceTable
// They may be overriden if datasets are rendered in other tables with different column layouts
const MAX_STEP_LINKS = 3;
const STEP_LINK_MAX_CHARACTER_COUNT = 70;
const DATASET_OR_SOURCE_MAX_ITEMS = 2;
const DATASET_MAX_CHARACTER_COUNT = 50;
const DATA_SOURCE_MAX_CHARACTER_COUNT = 48;

/**
 * Get index to truncate each name at, if names will overall spill past the specified limit.
 *
 * Returns null if no truncation is necessary.
 */
function getDatasetNameTruncationIndex(
  names: Array<string>,
  allowedLength: number,
  maxItems: number
): number | null {
  const shownNames = names.slice(0, maxItems);
  const totalNameLength = shownNames.reduce((acc: number, name: string) => acc + name.length, 0);
  return totalNameLength > allowedLength ? Math.floor(allowedLength / shownNames.length) : null;
}

export function TextWithIconListItem(props: {
  text: string;
  iconName: BenchlingIconName;
  ellipsizeAtIdx?: number | null;
  url?: string;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const text = props.ellipsizeAtIdx != null ? ellipsize(props.text, props.ellipsizeAtIdx) : props.text;
  const textWithIcon = (
    <div className={css(styles.itemInCell)} {...getTestProps(props.dataTestAttrs)}>
      <BenchlingIcon icon={props.iconName} className={css(styles.prependedIcon)} />
      {text}
    </div>
  );
  return props.url != null ? (
    <a href={props.url} target="blank" rel="noopener noreferrer">
      {textWithIcon}
    </a>
  ) : (
    textWithIcon
  );
}

/**
 * Renders a horizontal list of items with a popover. If the number of items exceeds the max number of items
 * to display, the count of extra items is included, and a popover is enabled.
 *
 * Caller can choose whether the popover should always be enabled or whether it should only be enabled when
 * the number of items exceeds the max number of items to display. forceEnablePopover allows showing the popover
 * under certain other conditions (e.g. if text was truncated).
 */
function DatasetListWithOverflow(props: {
  items: Array<JSX.Element>;
  onlyEnablePopoverOnOverflow: boolean;
  forceEnablePopover?: boolean;
  maxItems: number;
  // If not specified, the popover will render `items`
  overridePopoverItems?: Array<JSX.Element>;
}): JSX.Element {
  const maxItems = props.maxItems;
  const itemsToDisplay = props.items.slice(0, maxItems);
  const extraItemsCount = props.items.length - itemsToDisplay.length;

  const popoverContent = <div>{props.overridePopoverItems ?? props.items}</div>;

  return (
    <Popover
      interaction={FloatingInteraction.HOVER}
      placement={FloatingPlacement.TOP}
      renderContent={() => popoverContent}
      isDisabled={props.onlyEnablePopoverOnOverflow && extraItemsCount <= 0 && !props.forceEnablePopover}
    >
      <div
        className={css(styles.horizontalList)}
        {...getTestProps({component: 'DatasetListWithOverflow', element: 'root'})}
      >
        {itemsToDisplay}
        {extraItemsCount > 0 && <Badge color={BadgeColor.GRAY}>{`+${extraItemsCount}`}</Badge>}
      </div>
    </Popover>
  );
}

/**
 * Component used as data source column cell content for any non-chippable data sources
 */
export function DataSourcesWithIcons(props: {
  dataSources: Array<{name: string; stepName?: string; url?: string}>;
  iconName: BenchlingIconName;
  // Useful when the data source displayed should not have a popover unless there are too many items to display.
  onlyEnablePopoverOnOverflow?: boolean;
  maxItems?: number;
  maxCharacterCount?: number;
}): JSX.Element {
  const maxItems = props.maxItems ?? DATASET_OR_SOURCE_MAX_ITEMS;
  const maxCharacterCount = props.maxCharacterCount ?? DATA_SOURCE_MAX_CHARACTER_COUNT;
  const truncationIndex = getDatasetNameTruncationIndex(
    props.dataSources.map(({name}) => name),
    maxCharacterCount,
    maxItems
  );
  const dataTestAttrs: DataTestAttrs = {
    component: 'DataSourceName',
    element: 'root',
  };
  const sources = props.dataSources.map(({name, url}, i) => (
    <TextWithIconListItem
      text={name}
      key={i}
      iconName={props.iconName}
      ellipsizeAtIdx={truncationIndex}
      url={url}
      dataTestAttrs={{...dataTestAttrs, key: name}}
    />
  ));
  // Include the step name in the tooltip to help dismabiguate sources with the same name
  // For example, by default, registration tables are named {schema name} {autoincremented number within step doc}, so they are likely to have the same name in different steps.
  // Including the step name also helps the user find the relevant step for each source.
  const popoverItemsOverrides = props.dataSources.map(({name, stepName, url}, i) => {
    if (stepName == null || stepName.trim() === '') {
      return (
        <TextWithIconListItem
          text={name}
          key={i}
          iconName={props.iconName}
          url={url}
          dataTestAttrs={{...dataTestAttrs, key: name}}
        />
      );
    }
    const sourceNameWithStepName = `${name} (${stepName})`;
    return (
      <TextWithIconListItem
        text={sourceNameWithStepName}
        key={i}
        iconName={props.iconName}
        url={url}
        dataTestAttrs={{...dataTestAttrs, key: sourceNameWithStepName}}
      />
    );
  });

  return (
    <DatasetListWithOverflow
      items={sources}
      onlyEnablePopoverOnOverflow={Boolean(props.onlyEnablePopoverOnOverflow)}
      forceEnablePopover={truncationIndex != null}
      overridePopoverItems={popoverItemsOverrides}
      maxItems={maxItems}
    />
  );
}

export function DatasetCellContent(props: {datasetName: string; datasetId: string}): JSX.Element {
  const truncationIndex = getDatasetNameTruncationIndex(
    [props.datasetName],
    DATASET_MAX_CHARACTER_COUNT,
    DATASET_OR_SOURCE_MAX_ITEMS
  );
  const dataTestAttrs: DataTestAttrs = {
    component: 'DatasetName',
    element: 'root',
    key: props.datasetId,
  };
  const fullItem = (
    <TextWithIconListItem key={0} text={props.datasetName} iconName="dataset" dataTestAttrs={dataTestAttrs} />
  );
  const truncatedItem =
    truncationIndex != null ? (
      <TextWithIconListItem
        text={props.datasetName}
        key={0}
        iconName="dataset"
        ellipsizeAtIdx={truncationIndex}
        dataTestAttrs={dataTestAttrs}
      />
    ) : (
      fullItem
    );
  return (
    <DatasetListWithOverflow
      items={[truncatedItem]}
      onlyEnablePopoverOnOverflow={true}
      forceEnablePopover={truncationIndex != null}
      overridePopoverItems={[fullItem]}
      maxItems={DATASET_OR_SOURCE_MAX_ITEMS}
    />
  );
}

export function CreatedAtCellContent(props: {createdAt: string | null}): JSX.Element {
  return <span>{props.createdAt != null ? DateUtil.readableTime(props.createdAt) : 'N/A'}</span>;
}

export function NotUsedInAnalysis(): JSX.Element {
  return <div className={css(styles.defaultText)}>Not used in analysis</div>;
}

export function StepsCellContent<T extends DocumentInfo | StepInfo>(props: {
  stepsOrDocuments: Array<T>;
}): JSX.Element {
  function getListSeparator(listIndex: number, listLength: number, maxDisplayedSteps: number): string {
    if (listIndex === listLength - 1 || listIndex === maxDisplayedSteps - 1) {
      // no separator after last item displayed in list
      return ' ';
    } else if (listIndex === listLength - 2) {
      // "and" after penultimate item
      return listLength === 2 ? ' and ' : ', and ';
    }
    return ', ';
  }

  function getStepLinks<T extends DocumentInfo | StepInfo>(
    stepsOrDocuments: Array<T>,
    options: {maxDisplayedSteps: number; includeListSeparator: boolean; totalEllipsisCutoff?: number}
  ): {stepLinks: Array<JSX.Element>; hadTruncation: boolean} {
    const numSteps = stepsOrDocuments.length;
    // If there are multiple steps and all of them will be shown, then subtract some buffer for including "and"
    const bufferForSeparator =
      options.includeListSeparator && numSteps > 1 && numSteps <= options.maxDisplayedSteps ? 5 : 0;
    const truncationIndex =
      options.totalEllipsisCutoff != null
        ? getDatasetNameTruncationIndex(
            stepsOrDocuments.map((stepOrDoc) => stepOrDoc.name),
            options.totalEllipsisCutoff - bufferForSeparator,
            options.maxDisplayedSteps
          )
        : null;
    return {
      stepLinks: stepsOrDocuments.map((stepOrDoc, i) => {
        const stepText =
          truncationIndex != null ? ellipsize(stepOrDoc.name, truncationIndex) : stepOrDoc.name;
        return (
          <div key={stepOrDoc.id}>
            {isDocumentInfo(stepOrDoc) ? (
              <a target="blank" rel="noopener noreferrer" href={stepOrDoc.editUrl}>
                {stepText}
              </a>
            ) : (
              <span>{stepText}</span>
            )}
            {options.includeListSeparator && getListSeparator(i, numSteps, options.maxDisplayedSteps)}
          </div>
        );
      }),
      hadTruncation: truncationIndex != null,
    };
  }

  const uniqueStepsOrDocs = Array.from(new Set(props.stepsOrDocuments));
  const {stepLinks: items, hadTruncation} = getStepLinks(uniqueStepsOrDocs, {
    maxDisplayedSteps: MAX_STEP_LINKS,
    includeListSeparator: true,
    totalEllipsisCutoff: STEP_LINK_MAX_CHARACTER_COUNT,
  });
  const {stepLinks: popoverItems} = getStepLinks(uniqueStepsOrDocs, {
    maxDisplayedSteps: MAX_STEP_LINKS,
    includeListSeparator: false,
  });

  return (
    <DatasetListWithOverflow
      items={items}
      onlyEnablePopoverOnOverflow={true}
      forceEnablePopover={hadTruncation}
      overridePopoverItems={popoverItems}
      maxItems={MAX_STEP_LINKS}
    />
  );
}

const styles = StyleSheet.create({
  itemInCell: {
    paddingLeft: 5,
    paddingRight: 5,
  },
  prependedIcon: {
    marginRight: 4,
  },
  horizontalList: {
    display: 'flex',
    flexDirection: 'row',
    whiteSpace: 'pre-wrap',
    alignItems: 'center',
  },
  defaultText: {
    fontStyle: 'italic',
  },
});
